// src/hooks/useCaptureCreate.ts - 輕量級創建 Capture hook（僅供 Today 頁面使用）
import { useState, useCallback } from 'react';
import { Capture } from '../types/capture';
import { createCapture } from '../services/api/captures';
import { 
  createCaptureLocal,
  replaceTempId
} from '../services/indexedDB';
import {
  addToSyncQueue
} from '../services/syncQueueService';

/**
 * 輕量級 Capture 創建 hook
 * 專為 Today 頁面設計，只包含創建功能，無訂閱監聽
 * 避免與 Captures 頁面的完整 useCaptures hook 產生衝突
 */
export const useCaptureCreate = (userId: string) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline] = useState(navigator.onLine);

  /**
   * 創建 Capture（離線優先）
   * 與 useCaptures 中的實現保持一致，但更輕量
   */
  const handleCreateCapture = useCallback(async (content: string, timeBoxId?: string, timeBoxTitle?: string): Promise<Capture> => {
    setIsSubmitting(true);
    
    // 生成臨時 ID
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date();
    
    try {
      // 創建本地 Capture 對象
      const localCapture: Capture = {
        id: tempId,
        content,
        userId,
        processed: false,
        createdAt: now,
        updatedAt: now,
        timeBoxId: timeBoxId || undefined,
        timeBoxTitle: timeBoxTitle || undefined,
      };
      
      
      // 1. 立即保存到 IndexedDB
      await createCaptureLocal(localCapture);
      
      // 2. 處理同步（線上/離線）
      if (isOnline) {
        try {
          
          // 準備伺服器資料
          const captureData = {
            content,
            userId,
            timeBoxId,
            timeBoxTitle
          };
          
          // 創建到伺服器
          const serverCapture = await createCapture(captureData);
          
          
          // 替換 IndexedDB 中的臨時記錄
          await replaceTempId(tempId, serverCapture.id);
          
          return serverCapture;
          
        } catch (syncError) {
          console.warn('⚠️ Today 伺服器同步失敗，加入同步佇列:', syncError);
          
          // 同步失敗，加入佇列待後續處理
          await addToSyncQueue('CREATE', tempId, localCapture);
          
          // 仍返回本地版本
          return localCapture;
        }
      } else {
        
        // 離線模式：直接加入同步佇列
        await addToSyncQueue('CREATE', tempId, localCapture);
        
        return localCapture;
      }
      
    } catch (err) {
      console.error('Today 創建 Capture 失敗:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, isOnline]);

  return {
    handleCreateCapture,
    isSubmitting
  };
};